import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../api.js';
import { Button, Card, EmptyState, Field, ICONS, inputClass } from '../components/ui.js';

/**
 * Customer-facing workspace manager — list the account's workspaces and create a
 * new one. Gated entirely on the multi-workspace feature: an account without it
 * has only its single default workspace, so this page (and its nav entry) never
 * render and we bounce straight to the dashboard. Creation hits the same
 * `POST /v1/workspaces` the admin console uses, which re-checks the entitlement
 * server-side; the per-workspace billing rate stays operator-only (not set here).
 */
export function Workspaces() {
  const { workspacesEnabled, workspaces, client, refreshWorkspaces } = useAuth();
  const [name, setName] = useState('');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createdName, setCreatedName] = useState<string | null>(null);

  if (!workspacesEnabled) return <Navigate to="/" replace />;

  const create = async () => {
    const trimmed = name.trim();
    if (!trimmed || creating) return;
    setCreating(true);
    setError(null);
    setCreatedName(null);
    try {
      const ws = await client.createWorkspace(trimmed);
      setName('');
      setCreatedName(ws.name);
      await refreshWorkspaces();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to create workspace');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-700 text-white shadow-sm">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path
              d="M12 2 2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Workspaces</h1>
          <p className="text-sm text-neutral-500">
            Separate books of business (e.g. parent / child agencies). Each statement upload and
            report can be scoped to a workspace.
          </p>
        </div>
      </div>

      <Card>
        <h2 className="mb-4 font-semibold">Your workspaces</h2>
        {workspaces.length === 0 ? (
          <EmptyState
            icon={ICONS.inbox}
            title="No workspaces yet"
            hint="Create your first workspace below."
          />
        ) : (
          <ul className="divide-y divide-neutral-100 dark:divide-neutral-800">
            {workspaces.map((w) => (
              <li key={w.id} className="flex items-center justify-between py-3">
                <span className="text-sm font-medium">{w.name}</span>
                {w.isDefault && (
                  <span className="rounded-full bg-neutral-100 px-2 py-0.5 text-xs font-medium text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300">
                    Default
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card>
        <h2 className="mb-1 font-semibold">New workspace</h2>
        <p className="mb-4 text-sm text-neutral-500">
          Add a workspace to track a separate book separately. You can choose it as the upload
          target on the Files page and filter reports by it.
        </p>
        <form
          className="flex items-end gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            create();
          }}
        >
          <div className="flex-1">
            <Field label="Workspace name">
              <input
                className={inputClass}
                value={name}
                maxLength={120}
                placeholder="e.g. Northeast agency"
                onChange={(e) => {
                  setName(e.target.value);
                  setCreatedName(null);
                  setError(null);
                }}
              />
            </Field>
          </div>
          <Button type="submit" loading={creating} disabled={!name.trim()}>
            {creating ? 'Creating…' : 'Create workspace'}
          </Button>
        </form>
        {error && <p className="mt-3 text-sm text-rose-600">{error}</p>}
        {createdName && <p className="mt-3 text-sm text-emerald-600">Created “{createdName}” ✓</p>}
      </Card>
    </div>
  );
}
